import React, { forwardRef, type MouseEvent, type KeyboardEvent } from 'react';
import { styled } from '@compiled/react';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import Button from '@atlaskit/button';
import type { TriggerProps } from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import useMergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import { useIsHydrateRenderMode } from '@atlassian/jira-ssr-controller/src/use-is-page-hydrating.tsx';

export type FilterButtonProps = {
	isSelected?: boolean;
	isDisabled?: boolean;
	text: string;
	label: string;
	testId?: string;
	/**
	 * @deprecated This prop was for use with React Loosely Lazy.
	 * Cleanup arranged behind empanada_nin_remove_data-vc_from_filter-button once all of the following FGs are removed:
	 * - visual-refresh-headers-buttons
	 */
	vcId?: string;
	customTriggerProps?: TriggerProps; // when using as a custom trigger for some popups (like Share),
	onClick?: (e: MouseEvent | KeyboardEvent) => void;
};

export const FilterButton = forwardRef<HTMLElement, FilterButtonProps>(
	(
		{
			isSelected,
			onClick,
			testId,
			vcId, // cleanup with empanada_nin_remove_data-vc_from_filter-button
			isDisabled,
			text,
			label,
			customTriggerProps,
		}: FilterButtonProps,
		ref,
	) => {
		const mergedRefs = useMergeRefs(ref, customTriggerProps?.ref ?? null);
		const isHydrateRenderMode = useIsHydrateRenderMode();

		return (
			<Button
				isSelected={isSelected}
				onClick={onClick}
				testId={testId}
				isDisabled={isDisabled}
				iconAfter={
					<IconWrapper>
						<ChevronDownIcon label={label} color="currentColor" size="small" />
					</IconWrapper>
				}
				data-vc={
					fg('empanada_nin_remove_data-vc_from_filter-button')
						? undefined
						: `${vcId}-button${__SERVER__ && !isHydrateRenderMode ? '-ssr' : ''}`
				}
				{...customTriggerProps}
				ref={mergedRefs}
			>
				{text}
			</Button>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.span({
	display: 'inline-flex',
	alignItems: 'center',
	/* Offset inner icon padding to match designs, as changing icon size to small (contrary
    /* to what Atlaskit docs say) does reduce icon shape size, and we don't want that */
	marginRight: token('space.negative.075'),
});
